import { useQuery } from '@tanstack/react-query';
import { useOrg } from '@/features/team/OrgProvider';
import { fetchSubscription, type Suscripcion } from './subscription';

/**
 * El estado del plan, cuando hay algo que decir.
 *
 * Dos criterios gobiernan esta pantalla:
 *
 * · **Avisar antes, no el día que deja de andar.** Enterarse de que se terminó
 *   la prueba en el momento de guardar un trabajo, en el campo, es enterarse
 *   tarde. Por eso aparece en los últimos días, no al vencer.
 *
 * · **No estar siempre.** Un cartel permanente deja de leerse, y el día que
 *   importa tampoco lo va a leer nadie. Con el plan al día y sin nada por
 *   vencer, acá no se muestra nada.
 */

/** Desde cuántos días antes conviene avisar que se termina la prueba. */
const AVISAR_DESDE = 7;

interface Aviso {
  tono: 'informativo' | 'atencion' | 'bloqueo';
  titulo: string;
  detalle: string;
}

export function avisoDe(s: Suscripcion): Aviso | null {
  if (!s.puede_escribir) {
    // Lo primero es decir qué se puede seguir haciendo. Alguien que ve
    // "bloqueado" sin más asume que perdió los datos, y no perdió nada.
    return {
      tono: 'bloqueo',
      titulo:
        s.estado === 'trialing'
          ? 'Se terminó la prueba de 30 días'
          : s.estado === 'past_due'
            ? 'No pudimos cobrar la suscripción'
            : 'La suscripción no está activa',
      detalle:
        'Podés seguir entrando, consultando y exportando todo. Tu información está intacta. ' +
        'Para volver a cargar y modificar hace falta activar el plan.',
    };
  }

  if (s.estado === 'past_due' && s.dias_restantes !== null) {
    return {
      tono: 'atencion',
      titulo: 'Hubo un problema con el cobro',
      detalle:
        s.dias_restantes === 0
          ? 'Hoy es el último día para regularizarlo. Después la cuenta pasa a sólo lectura.'
          : `Podés seguir trabajando ${s.dias_restantes} día(s) más mientras se regulariza.`,
    };
  }

  if (s.estado === 'trialing' && s.dias_restantes !== null && s.dias_restantes <= AVISAR_DESDE) {
    return {
      tono: 'informativo',
      titulo:
        s.dias_restantes === 0
          ? 'Hoy es el último día de la prueba'
          : `Quedan ${s.dias_restantes} día(s) de prueba`,
      detalle:
        'Cuando termine vas a poder seguir consultando todo, pero no cargar trabajos nuevos.',
    };
  }

  return null;
}

const ESTILO: Record<Aviso['tono'], string> = {
  informativo: 'border-sky-300 bg-sky-50 text-sky-950',
  atencion: 'border-[var(--aviso)] bg-[var(--aviso-fondo)] text-[var(--aviso)]',
  bloqueo: 'border-red-300 bg-[var(--peligro-fondo)] text-red-950',
};

export function BannerSuscripcion() {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id;

  const { data } = useQuery({
    queryKey: ['suscripcion', orgId],
    queryFn: () => fetchSubscription(orgId!),
    enabled: !!orgId,
    // Una vez por hora alcanza: el plan no cambia entre pantalla y pantalla.
    staleTime: 60 * 60 * 1000,
  });

  if (!data) return null;
  const aviso = avisoDe(data);
  if (!aviso) return null;

  return (
    <div
      role="status"
      className={`mx-4 mt-3 superficie px-4 py-3 text-sm ${ESTILO[aviso.tono]}`}
      data-testid="banner-suscripcion"
    >
      <p className="font-semibold">{aviso.titulo}</p>
      <p className="mt-0.5 opacity-90">{aviso.detalle}</p>
      {data.asientos !== null && data.asientos_ocupados !== null && (
        <p className="mt-1 text-xs text-[var(--tinta-suave)]">
          {data.asientos_ocupados} de {data.asientos} usuario(s) del plan.
        </p>
      )}
    </div>
  );
}
